import { clone } from 'lodash';

import { TableColumnConfig } from 'models/tableView.model';
import { stringify } from 'utils/base64';

export interface ColumnViewState {
  key: string;
  hidden?: boolean;
  customCheckboxCell?: boolean;
}

export function snapshotColumns<T extends ColumnViewState>(
  columns: (T | null)[] | undefined
): TableColumnConfig[] {
  if (!columns?.length) {
    return [];
  }

  return columns.reduce<TableColumnConfig[]>((acc, column) => {
    if (!column || column.customCheckboxCell) {
      return acc;
    }

    acc.push(column.hidden ? { key: column.key, hidden: true } : { key: column.key });
    return acc;
  }, []);
}

export function serializeColumnsForUrl<T extends ColumnViewState>(
  columns: (T | null)[] | undefined
): string | undefined {
  const snapshot = snapshotColumns(columns);

  if (!snapshot.length) {
    return undefined;
  }

  return stringify(snapshot);
}

export function isFullColumnConfig<T extends ColumnViewState>(
  config: TableColumnConfig[],
  columns: (T | null)[]
): boolean {
  const flexibleKeys = columns
    .filter((column): column is T => !!column && !column.customCheckboxCell)
    .map((column) => column.key);

  if (!flexibleKeys.length || !config.length) {
    return false;
  }

  const configKeys = new Set(config.map((item) => item.key));
  return flexibleKeys.every((key) => configKeys.has(key));
}

export function applyColumnConfig<T extends ColumnViewState>(
  columns: (T | null)[],
  config: TableColumnConfig[]
): (T | null)[] {
  if (!config.length) {
    return resetColumnVisibility(columns);
  }

  const fixed = columns.filter((column) => column?.customCheckboxCell);
  const flexible = columns.filter((column): column is T => !!column && !column.customCheckboxCell);

  if (!isFullColumnConfig(config, columns)) {
    const hiddenKeys = new Set(config.filter((item) => item.hidden).map((item) => item.key));

    return columns.map((column) => {
      if (!column || column.customCheckboxCell) {
        return column;
      }

      if (hiddenKeys.has(column.key)) {
        return column.hidden ? column : { ...column, hidden: true };
      }

      if (column.hidden) {
        const { hidden: _hidden, ...rest } = column;
        return rest as T;
      }

      return column;
    });
  }

  const byKey = new Map(flexible.map((column) => [column.key, column]));
  const ordered: T[] = [];

  config.forEach((item) => {
    const column = byKey.get(item.key);
    if (!column) {
      return;
    }

    byKey.delete(item.key);

    if (item.hidden) {
      ordered.push({ ...column, hidden: true });
      return;
    }

    if (column.hidden) {
      const { hidden: _hidden, ...rest } = column;
      ordered.push(rest as T);
      return;
    }

    ordered.push(column);
  });

  byKey.forEach((column) => {
    ordered.push(column);
  });

  return [...fixed, ...ordered];
}

export function resetColumnVisibility<T extends ColumnViewState>(
  columns: (T | null)[]
): (T | null)[] {
  return columns.map((column) => {
    if (!column?.hidden) {
      return column;
    }

    const { hidden: _hidden, ...rest } = column;
    return rest as T;
  });
}

export function resetColumnsToDefault<T extends ColumnViewState>(
  defaultColumns: (T | null)[]
): (T | null)[] {
  return clone(defaultColumns);
}

export type ColumnDropPosition = 'before' | 'after';

export function reorderColumnsByKey<T extends ColumnViewState>(
  columns: (T | null)[],
  fromKey: string,
  toKey: string,
  position: ColumnDropPosition = 'before'
): (T | null)[] {
  if (fromKey === toKey) {
    return columns;
  }

  const next = [...columns];
  const fromIndex = next.findIndex((column) => column?.key === fromKey);
  let toIndex = next.findIndex((column) => column?.key === toKey);

  if (fromIndex < 0 || toIndex < 0) {
    return columns;
  }

  const [moved] = next.splice(fromIndex, 1);
  if (fromIndex < toIndex) {
    toIndex -= 1;
  }

  const insertIndex = position === 'after' ? toIndex + 1 : toIndex;
  next.splice(insertIndex, 0, moved);
  return next;
}

export function areColumnConfigsEqual(
  left: TableColumnConfig[],
  right: TableColumnConfig[]
): boolean {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((item, index) => {
    const other = right[index];
    return item.key === other.key && !!item.hidden === !!other.hidden;
  });
}
